import { useState } from "react"
import { useAction } from "convex/react"
import { api } from "../../../../convex/_generated/api"
import {
  useStorageUpload,
  type UploadedAssetRef,
} from "@/hooks/use-storage-upload"

/** PDF/DOCX/TXT — matches job_document's contentTypes in convex/lib/assets.ts. */
export const JOB_DOCUMENT_ACCEPT = ".pdf,.docx,.txt"
/** Keep in sync with job_document's maxBytes in convex/lib/assets.ts. */
const MAX_JOB_DOCUMENT_BYTES = 10 * 1024 * 1024

export type AttachedJobDocument = UploadedAssetRef & { fileName: string }

/**
 * The "Draft with AI" data layer for the job create form: an optional JD
 * document upload plus the two draft actions behind one call. The uploaded
 * file is transient — nothing is stored on the job, and the claim is left to
 * expire — so re-drafting from the same file just calls `draft` again.
 */
export function useJobAiDraft() {
  const draftFromNotes = useAction(api.jobsActions.draftJobDescription)
  const draftFromDocument = useAction(
    api.jobDocumentActions.draftJobFromDocument,
  )
  const [document, setDocument] = useState<AttachedJobDocument | null>(null)

  const { fileInputRef, uploading, upload } = useStorageUpload({
    purpose: "job_document",
    validate: (file) =>
      file.size > MAX_JOB_DOCUMENT_BYTES ? "File must be under 10 MB" : null,
    onUploaded: (ref, file) => setDocument({ ...ref, fileName: file.name }),
  })

  const draft = (args: { notes: string; title?: string }) =>
    document
      ? draftFromDocument({
          claimId: document.claimId,
          storageId: document.storageId,
          title: args.title,
          notes: args.notes.trim() || undefined,
        })
      : draftFromNotes({ notes: args.notes, title: args.title })

  return {
    document,
    clearDocument: () => setDocument(null),
    fileInputRef,
    uploading,
    uploadDocument: upload,
    draft,
  }
}
